import { supabaseService } from '../db/supabase';
import type { AppState } from '../types';

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const VIEWS_VERIFICACAO = [
  { key: 'resultado_semtombo', nome: '1 - Sem Tombo' },
  { key: 'resultado_duplicados', nome: '2 - Duplicados' },
  { key: 'resultado_danificados', nome: '3 - Danificados' },
  { key: 'resultado_localdiverso', nome: '4 - Fora do Local' },
  { key: 'resultado_porlocal', nome: '5 - Por Local' },
  { key: 'resultado_naoencontrados', nome: '6 - Não Encontrados' },
];

export class ModuloVerificacao {
  constructor(private state: AppState) {}

  inicializar() {
    this.state.verifView = null; // Reseta a view ao inicializar
    this.renderizarSubmodulos();
    this.vincularEventos();
    this.esconderSwitcher();
  }

  private renderizarSubmodulos() {
    const grid = byId('verif-submodulos');
    const titulo = byId('verif-submodulos-titulo');
    if (!grid || !titulo) return;

    grid.innerHTML = '';
    grid.classList.remove('modulos-slide-up');
    grid.style.display = 'grid'; // Garante que está visível
    titulo.style.display = 'block';

    VIEWS_VERIFICACAO.forEach(view => {
      const card = this.criarCard(
        view.nome,
        '',
        () => this.selecionarSubmodulo(view.key),
        false // Nenhum ativo inicialmente
      );
      grid.appendChild(card);
    });
  }

  private selecionarSubmodulo(viewKey: string) {
    const grid = byId('verif-submodulos');
    const titulo = byId('verif-submodulos-titulo');

    // Se o grid já estiver escondido, é um clique no switcher, então só carrega
    if (grid && grid.style.display === 'none') {
      this.carregarSubmodulo(viewKey);
    } else if (grid && titulo) {
      // Se for o primeiro clique, anima e depois carrega
      grid.classList.add('modulos-slide-up');
      setTimeout(() => {
        grid.style.display = 'none';
        titulo.style.display = 'none';
        this.carregarSubmodulo(viewKey);
      }, 400);
    }
  }

  private async carregarSubmodulo(viewKey: string) {
    this.state.verifView = viewKey;
    this.renderizarSubmoduloSwitcher(); // Atualiza e mostra o switcher

    const box = byId('lista-verificacao');
    const locaisGrid = byId('verif-locais');

    if (!box || !locaisGrid) return;
    if (!this.state.campus) {
      box.textContent = 'Selecione um campus primeiro.';
      return;
    }

    box.textContent = 'Carregando dados...';
    locaisGrid.innerHTML = '';

    try {
      const data = await supabaseService.buscarDadosVerificacao(
        this.state.campus.id,
        viewKey
      );

      if (!data || data.length === 0) {
        box.textContent = 'Sem dados para este submódulo.';
        return;
      }

      const grupos = new Map<string, any[]>();
      data.forEach(item => {
        const chave = this.chaveLocal(item);
        if (!grupos.has(chave)) grupos.set(chave, []);
        grupos.get(chave)!.push(item);
      });

      locaisGrid.innerHTML = '';
      [...grupos.keys()].sort().forEach(local => {
        const itens = grupos.get(local)!;
        const card = this.criarCard(
          local,
          `${itens.length} registro(s)`,
          () => this.mostrarItensLocal(itens, local)
        );
        locaisGrid.appendChild(card);
      });

      box.textContent = 'Selecione um local para ver os itens.';
    } catch (error) {
      console.error('Erro ao carregar dados de verificação:', error);
      box.textContent = `Erro: ${(error as Error).message}`;
    }
  }
  
  private renderizarSubmoduloSwitcher() {
    const switcher = byId('verif-switcher');
    if (!switcher) return;

    switcher.innerHTML = '';
    VIEWS_VERIFICACAO.forEach(view => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `modulo-chip ${this.state.verifView === view.key ? 'active' : ''}`;
      btn.textContent = view.nome;
      btn.addEventListener('click', () => this.selecionarSubmodulo(view.key));
      switcher.appendChild(btn);
    });

    switcher.classList.remove('hidden');
  }
  
  private esconderSwitcher() {
    const switcher = byId('verif-switcher');
    if (switcher) switcher.classList.add('hidden');
  }

  private mostrarItensLocal(itens: any[], local: string) {
    const box = byId('lista-verificacao');
    if (!box) return;

    box.innerHTML = itens.map(item => `
      <div class="list-item">
        <strong>${item.tombo || item.numero || '-'}</strong> ·
        ${item.descricao || item.bem || '-'}<br>
        <small>${local}</small>
      </div>
    `).join('');
  }

  private async carregarTodos() {
    if (!this.state.verifView) {
      alert('Escolha um submódulo primeiro.');
      return;
    }
    if (!this.state.campus) return;

    const box = byId('lista-verificacao');
    if (!box) return;
    box.textContent = 'Carregando todos os registros...';

    try {
      const data = await supabaseService.buscarDadosVerificacao(
        this.state.campus.id,
        this.state.verifView
      );
      box.innerHTML = (data || []).map(item => `
        <div class="list-item">
          <strong>${item.tombo || item.numero || '-'}</strong> ·
          ${item.descricao || item.bem || '-'}<br>
          <small>${this.chaveLocal(item)}</small>
        </div>
      `).join('') || 'Sem dados.';
    } catch (error) {
      console.error('Erro ao carregar todos:', error);
      box.textContent = `Erro: ${(error as Error).message}`;
    }
  }

  private chaveLocal(item: any): string {
    return [
      item.local_campus, item.local_sede, item.local_bloco,
      item.local_andar, item.local_sala, item.sala
    ].filter(Boolean).join(' / ') || 'Sem local';
  }

  private criarCard(
    titulo: string,
    descricao: string,
    onClick: () => void,
    ativo = false
  ): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `card ${ativo ? 'active-choice' : ''}`;
    btn.innerHTML = `
      <strong>${titulo}</strong>
      ${descricao ? `<small>${descricao}</small>` : ''}
    `;
    btn.addEventListener('click', onClick);
    return btn;
  }

  private vincularEventos() {
    const btnTodos = byId('btn-verif-todos');
    btnTodos?.addEventListener('click', () => this.carregarTodos());
  }
}
